import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal, NotificationAlert } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { ModalSize } from "src/enumerations/ModalSize";
import { InstitutionItemsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionActions from "../../../actions/institution";
import * as InstitutionItemActions from "../../../actions/institutionItem";
import * as InstitutionItemCategoryActions from "../../../actions/institutionItemCategory";
import * as InstitutionItemTemplateActions from "../../../actions/institutionItemTemplate";
import * as NavigationActions from "../../../actions/navigation";
import * as InstitutionItemsPageActions from "../../../actions/pages/institutionItemsPage";
import { SelectFormInput } from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import CollectionInstanceComponent from "../../../containers/Collection/CollectionInstance/CollectionInstanceComponent";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { setModalTitle } from "../../../helpers/modalHelper";
import { InstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import InstitutionItemCreateVersion from "../InstitutionItemCreateVersion/InstitutionItemCreateVersion";

import "bootstrap/dist/js/bootstrap.bundle.min";

import "./InstitutionItemsPage.scss";

interface InstitutionItemsPageProps {
    institution: Dtos.Institution;
    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    collectionInstanceModalOpen: boolean;
    collectionInstanceId: number;
    modalTitle: string;
    modalDescription: string;
    institutionItemTemplates: Dtos.InstitutionItemTemplate[];
    loadingInstitutionItemTemplates: boolean;
    loadInstitutionItemTemplatesSuccess: boolean;
    loadInstitutionItemTemplatesFailure: boolean;
    loadInstitutionItemTemplatesErrorCode: string;
    savingInstitutionItem: boolean;
    saveInstitutionItemSuccess: boolean;
    createVersionModalOpen: boolean;
    createVersionInstitutionItem: Dtos.InstitutionItem;
    institutionItemCategories: Dtos.InstitutionItemCategory[];
    loadingInstitutionItemCategories: boolean;
    loadInstitutionItemCategoriesSuccess: boolean;
    loadInstitutionItemCategoriesFailure: boolean;
    loadInstitutionItemCategoriesErrorCode: string;
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearInstitutionItemTemplates: InstitutionItemTemplateActions.IInstitutionItemTemplateClearActionCreator;
    loadInstitutionItemTemplates: InstitutionItemTemplateActions.IInstitutionItemTemplateLoadActionCreator;
    saveInstitutionItem: InstitutionItemActions.IInstitutionItemSaveActionCreator;
    setEditModelViewState: InstitutionItemsPageActions.IInstitutionItemsPageSetEditModalViewStateActionCreator;
    setCreateVersionModelViewState: InstitutionItemsPageActions.IInstitutionItemsPageSetCreateVersionModalViewStateActionCreator;
    clearInstitutionItemCategories: InstitutionItemCategoryActions.IInstitutionItemCategoriesClearActionCreator;
    loadInstitutionItemCategories: InstitutionItemCategoryActions.IInstitutionItemCategoriesLoadByInstitutionIdActionCreator;
}

const InstitutionItemsPage: FC<InstitutionItemsPageProps> = ({
    institution,
    loadingInstitution,
    loadInstitutionSuccess,
    loadInstitutionFailure,
    loadInstitutionErrorCode,
    permissions,
    collectionInstanceModalOpen,
    collectionInstanceId,
    modalTitle,
    modalDescription,
    institutionItemTemplates,
    loadingInstitutionItemTemplates,
    loadInstitutionItemTemplatesSuccess,
    loadInstitutionItemTemplatesFailure,
    loadInstitutionItemTemplatesErrorCode,
    savingInstitutionItem,
    saveInstitutionItemSuccess,
    createVersionModalOpen,
    createVersionInstitutionItem,
    institutionItemCategories,
    loadingInstitutionItemCategories,
    loadInstitutionItemCategoriesSuccess,
    loadInstitutionItemCategoriesFailure,
    loadInstitutionItemCategoriesErrorCode,
    clearInstitution,
    loadInstitution,
    setBreadcrumbs,
    setDrawerItems,
    clearInstitutionItemTemplates,
    loadInstitutionItemTemplates,
    saveInstitutionItem,
    setEditModelViewState,
    setCreateVersionModelViewState,
    clearInstitutionItemCategories,
    loadInstitutionItemCategories
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- States --
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [addInstitutionItemId, setAddInstitutionItemId] = useState<number | undefined>(undefined);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showInstitutionItems =
        !loadingInstitution &&
        loadInstitutionSuccess &&
        !loadingInstitutionItemTemplates &&
        loadInstitutionItemTemplatesSuccess &&
        !loadingInstitutionItemCategories &&
        loadInstitutionItemCategoriesSuccess &&
        !creatingTree;


    // Load Institution & Institution Items
    useEffect(() => {
        if (institutionIdNumber && !institution) {
            setBreadcrumbs(InstitutionItemsBreadcrumbs(institutionIdNumber));
            setDrawerItems(
                InstitutionSubMenu(institutionIdNumber, permissions)
            );

            loadInstitution(institutionIdNumber);
            loadInstitutionItemTemplates(institutionIdNumber);
            loadInstitutionItemCategories(institutionIdNumber);
        }
    }, [
        institutionIdNumber,
        permissions,
        institution,
        setBreadcrumbs,
        setDrawerItems,
        loadInstitution,
        InstitutionItemsBreadcrumbs,
        InstitutionSubMenu,
        loadInstitutionItemTemplates,
        loadInstitutionItemCategories
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }
        if (loadInstitutionItemTemplatesFailure) {
            navigate(`/error/${loadInstitutionItemTemplatesErrorCode}`);
        }
        if (loadInstitutionItemCategoriesFailure) {
            navigate(`/error/${loadInstitutionItemCategoriesErrorCode}`);
        }
    }, [
        loadInstitutionFailure,
        loadInstitutionItemTemplatesFailure,
        loadInstitutionErrorCode,
        loadInstitutionItemTemplatesErrorCode,
        loadInstitutionItemCategoriesFailure,
        loadInstitutionItemCategoriesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionIdNumber && institution?.name) {
            setDrawerItems(
                InstitutionSubMenu(institutionIdNumber, permissions)
            );
            setBreadcrumbs(
                InstitutionItemsBreadcrumbs(
                    institutionIdNumber,
                    institution.name
                )
            );
        }
    }, [
        institutionIdNumber,
        institution,
        permissions,
        InstitutionSubMenu,
        InstitutionItemsBreadcrumbs,
        setDrawerItems,
        setDrawerItems,
    ]);

    // Cleanup effect to clear the institution item on component unmount
    useEffect(() => {
        return () => {
            clearInstitution();
            clearInstitutionItemTemplates();
            clearInstitutionItemCategories();
        };
    }, [clearInstitution, clearInstitutionItemTemplates, clearInstitutionItemCategories]);

    // Reload institution & institution items after saving institution item
    useEffect(() => {
        if (!savingInstitutionItem && saveInstitutionItemSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution item saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );
            loadInstitution(institutionIdNumber);
            loadInstitutionItemTemplates(institutionIdNumber);
            loadInstitutionItemCategories(institutionIdNumber);
            setInitalizedTreeData(false);
        }
    }, [
        savingInstitutionItem,
        saveInstitutionItemSuccess,
        institutionIdNumber,
        loadInstitution,
        loadInstitution,
        loadInstitutionItemTemplates,
        setInitalizedTreeData,
        loadInstitutionItemCategories
    ]);

    // Refresh tree
    useEffect(() => {
        if (institutionItemCategories) {
            setInitalizedTreeData(false);
        }
    }, [institutionItemCategories, setInitalizedTreeData]);

    const handleModalClose = () => {
        setIsOpen(false);
        setAddInstitutionItemId(undefined);
    };

    const handleModalSubmit = async () => {
        if (!addInstitutionItemId || !institutionIdNumber) {
            Alert.error(
                <NotificationAlert
                    alertContent={<span>An error occurred saving the institution item</span>}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
            return;
        }
        let template = institutionItemTemplates.find((x) => x.id === addInstitutionItemId);
        let institutionItem: Dtos.InstitutionItem = {
            institutionId: institutionIdNumber,
            institutionItemTemplateId: addInstitutionItemId,
            version: template?.initialVersion ?? null,
            active: true,
        } as Dtos.InstitutionItem;
        await saveInstitutionItem(institutionItem, null);

        setIsOpen(false);
        setAddInstitutionItemId(undefined);
    };

    // Create tree
    useEffect(() => {
        if (institutionItemCategories && !initalizedTreeData) {
            setCreatingTree(true);

            // If no institution items return
            if (institutionItemCategories?.length == 0) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (
                institutionItemSummaries: Dtos.InstitutionItemSummary[]
            ) => {
                let children = [];
                const institutionedSummaries =
                    Object.values<Dtos.InstitutionItemSummary>(
                        institutionItemSummaries.reduce((acc, item) => {
                            const key = `${item.institutionId}-${item.institutionItemTemplateId}`;

                            if (!acc[key] || new Date(item.dateEntered) > new Date(acc[key].dateEntered)) {
                                acc[key] = item;
                            }

                            return acc;
                        }, {})
                    );

                children =
                    institutionedSummaries
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 2fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.FILE} />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <span
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </span>
                                    </div>
                                    <div className="px-1">
                                        {y?.version && (
                                            <button
                                                disabled={
                                                    !AuthenticationHelper.hasPermission(
                                                        permissions,
                                                        Dtos.Permission.InstitutionItemUpdate,
                                                        null,
                                                        null
                                                    )
                                                }
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm btn-row-version"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (y?.collectionInstanceId) {
                                                        setEditModelViewState(
                                                            true,
                                                            y.collectionInstanceId,
                                                            y?.name,
                                                            y?.description
                                                        );
                                                    }
                                                }}
                                            >
                                                <span>{y.version}</span>
                                            </button>
                                        )}
                                        {y?.institutionItemSummaryVersions?.map(
                                            (item, index) => (
                                                <button
                                                    key={`version-button-${item?.version}-${index}`}
                                                    className="btn btn-secondary btn-sm btn-row-version"
                                                    type="button"
                                                    disabled={
                                                        !AuthenticationHelper.hasPermission(
                                                            permissions,
                                                            Dtos.Permission.InstitutionItemUpdate,
                                                            null,
                                                            null
                                                        )
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (item?.collectionInstanceId) {
                                                            setEditModelViewState(
                                                                true,
                                                                item.collectionInstanceId,
                                                                y?.name,
                                                                y?.description
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <span>{item.version}</span>
                                                </button>
                                            )
                                        )}
                                    </div>
                                    <div className="px-1 d-flex align-items-start justify-content-end">
                                        {y?.allowVersions && (
                                            <button
                                                disabled={false}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const institutionItems = institutionItemCategories?.flatMap(x => x.institutionItems) ?? [];
                                                    setCreateVersionModelViewState(
                                                        true,
                                                        institutionItems.find((x) => x.id == y.id)
                                                    );
                                                }}
                                                type="button"
                                                className="btn btn-outline-secondary mr-2 btn-sm"
                                            >
                                                <span>New version</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 2fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Version</strong>
                                </div>
                                <div className="px-1"></div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                [...institutionItemCategories]
                    ?.sort((a, b) => a?.institutionItemTemplateCategory?.rank - b?.institutionItemTemplateCategory?.rank)
                    ?.map((x) => ({
                        id: `category-${x?.institutionItemTemplateCategory?.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ROLE} />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "400px" }}
                                >
                                    {x?.institutionItemTemplateCategory?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        { [...new Set(x?.institutionItems?.map((x) => x.institutionItemTemplateId)),]?.length }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.institutionItemSummaries),],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        institutionItemCategories,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                } else if (id.includes("item")) {
                    const institutionItems = institutionItemCategories?.flatMap(x => x.institutionItems) ?? [];
                    const selectedInstitutionItem: Dtos.InstitutionItem = institutionItems?.find((x) => x.id?.toString() == id.replace("item-", ""));
                    setEditModelViewState(
                        true,
                        selectedInstitutionItem?.collectionInstanceId,
                        selectedInstitutionItem?.institutionItemTemplate?.name,
                        selectedInstitutionItem?.institutionItemTemplate
                            ?.description
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions, institutionItemCategories]
    );

    const renderCreateVersionModal = useMemo(() => {
        if (institution && !loadingInstitution) {
            return (
                <Modal
                    open={createVersionModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setCreateVersionModelViewState(false, null);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {setModalTitle(undefined, "Create Version")}
                        </h5>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div
                        className="modal-body"
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                        <InstitutionItemCreateVersion
                            institutionId={institutionIdNumber}
                            currentInstitutionItem={createVersionInstitutionItem}
                            onClose={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
    }, [
        institutionIdNumber,
        loadingInstitution,
        createVersionModalOpen,
        setCreateVersionModelViewState,
        loadInstitution,
        createVersionInstitutionItem,
    ]);

    const renderItemModal = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.InstitutionItemUpdate,
            undefined,
            undefined
        );
        const formDisabled = !hasPermission;

        if (collectionInstanceId && collectionInstanceId > 0) {
            return (
                <Modal
                    open={collectionInstanceModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setEditModelViewState(false, null, "", "");
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h5 className="mb-0">
                                {setModalTitle(undefined, modalTitle)}
                            </h5>
                            {modalDescription ? (
                                <small>
                                    {modalDescription}
                                </small>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body customscrollbar">
                        <CollectionInstanceComponent 
                            collectionInstanceId={collectionInstanceId}
                            readonly={formDisabled}
                            onClose={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                            onSaved={() => {
                                loadInstitutionItemCategories(institutionIdNumber);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        collectionInstanceId,
        collectionInstanceModalOpen,
        permissions,
        modalTitle,
        modalDescription,
        institutionIdNumber,
        setEditModelViewState,
        loadInstitutionItemCategories,
    ]);

    const selectOptions = useMemo(() => {
        if (!institutionItemTemplates || !institutionItemCategories) return [];

        const institutionItems = institutionItemCategories?.flatMap(x => x.institutionItems) ?? [];
        const currentIds = [...new Set(institutionItems?.map((x) => x.institutionItemTemplateId))];

        var options = institutionItemTemplates
            ?.filter((x) => !currentIds.includes(x.id))
            ?.map((x) => ({ value: x.id, label: x?.initialVersion ? `${x?.name} | ${x.initialVersion}` : x?.name }));
        
        return options?.sort((a, b) => a?.label?.localeCompare(b?.label));
    }, [institutionItemTemplates, institutionItemCategories]);

    return (
        <RestrictedLayout
            loading={loadingInstitution || loadingInstitutionItemTemplates || loadingInstitutionItemCategories}
            permission={Dtos.Permission.InstitutionItemView}
        >
            <div>
                <div className="row">
                    <div className={"col mb-2"}>
                        <h1>Institution Items</h1>
                    </div>
                    <div className="col-auto">
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InstitutionItemCreate,
                            institutionIdNumber,
                            null
                        ) && (
                            <>
                                <div className="btn-group">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setIsOpen(true)}
                                    >
                                        Add Item Template
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                                        data-toggle="dropdown"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                    >
                                        <span className="sr-only">
                                            Toggle Dropdown
                                        </span>
                                    </button>
                                    <div className="dropdown-menu dropdown-menu-right">
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/institution/itemtemplates/${institutionIdNumber}/template/create`)
                                            }
                                        >
                                            Create Item Template
                                        </a>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/institution/itemtemplates/${institutionIdNumber}`)
                                            }
                                        >
                                            Edit Item Templates
                                        </a>
                                        <div className="dropdown-divider"></div>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() => setIsOpen(true)}
                                        >
                                            Add Item Template
                                        </a>
                                    </div>
                                </div>

                                <Modal
                                    open={isOpen}
                                    disableCloseOnOverlayClick={true}
                                    onClose={handleModalClose}
                                    size={ModalSize.Lg}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            Add Institution Item
                                        </h5>
                                        <button
                                            type="button"
                                            className="close"
                                            aria-label="Close"
                                            onClick={handleModalClose}
                                        >
                                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                                        </button>
                                    </div>
                                    <div className="modal-body p-3">
                                        <div className="form-row pb-2">
                                            <div className="col-12">
                                                {selectOptions?.length === 0 ? (
                                                    <>
                                                        <span>
                                                            No <em>Institution Item Templates</em> are available for <strong>{institution?.name}</strong>.
                                                        </span>
                                                        <br />
                                                        <button
                                                            className="btn btn-primary mr-1 mt-4"
                                                            onClick={() =>
                                                                navigate(`/settings/institution/itemtemplates/${institutionIdNumber}/template/create`)
                                                            }
                                                        >
                                                            Create Item Template
                                                        </button>
                                                    </>
                                                ) : (
                                                    <SelectFormInput
                                                        loading={loadingInstitutionItemTemplates}
                                                        multi={false}
                                                        options={selectOptions}
                                                        clearable={true}
                                                        backspaceRemoves={false}
                                                        name={"addInstitutionItemId"}
                                                        id={"addInstitutionItemId"}
                                                        placeholder="Institution Item"
                                                        onChange={function (value: any, _options?: any): any {
                                                            setAddInstitutionItemId(
                                                                value != undefined
                                                                    ? parseInt(value)
                                                                    : undefined
                                                            );
                                                        }}
                                                        value={addInstitutionItemId}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-row pt-2">
                                            <div className="col-12 d-flex justify-content-end">
                                                {selectOptions?.length > 0 && (
                                                    <button
                                                        className="btn btn-primary mr-1"
                                                        disabled={savingInstitutionItem}
                                                        onClick={handleModalSubmit}
                                                    >
                                                        Submit
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-outline-primary ml-1"
                                                    onClick={handleModalClose}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Modal>
                            </>
                        )}
                    </div>
                </div>
                {!showInstitutionItems ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutionItems ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                        {renderItemModal}
                        {renderCreateVersionModal}
                    </>
                ) : null}
            </div>
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionItemsPageProps> => {
    let institution: Dtos.Institution | undefined = !(
        state.institutions.formData instanceof Array
    )
        ? state.institutions.formData
        : undefined;
    let institutionItemCategories: Dtos.InstitutionItemCategory[] | undefined = (state.institutionItemCategory.formData instanceof Array)
            ? state.institutionItemCategory.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institution: institution!,
        loadingInstitution: state.institutions.loadState && state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess: state.institutions.loadState && state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure: state.institutions.loadState && state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,
        institutionItemCategories: institutionItemCategories!,
        loadingInstitutionItemCategories: state.institutionItemCategory.loadState && state.institutionItemCategory.loadState.status == RequestState.Pending,
        loadInstitutionItemCategoriesSuccess: state.institutionItemCategory.loadState && state.institutionItemCategory.loadState.status == RequestState.Success,
        loadInstitutionItemCategoriesFailure: state.institutionItemCategory.loadState && state.institutionItemCategory.loadState.status == RequestState.Failure,
        loadInstitutionItemCategoriesErrorCode: state.institutionItemCategory.loadState.errorCode,
        permissions: permissions,
        collectionInstanceModalOpen: state.institutionItemsPage.editModalOpen,
        collectionInstanceId: state.institutionItemsPage.collectionInstanceId,
        modalTitle: state.institutionItemsPage.name,
        modalDescription: state.institutionItemsPage.description,
        institutionItemTemplates: state.institutionItemTemplate.formFilteredData!,
        loadingInstitutionItemTemplates: state.institutionItemTemplate.loadState && state.institutionItemTemplate.loadState.status == RequestState.Pending,
        loadInstitutionItemTemplatesSuccess: state.institutionItemTemplate.loadState && state.institutionItemTemplate.loadState.status == RequestState.Success,
        loadInstitutionItemTemplatesFailure: state.institutionItemTemplate.loadState && state.institutionItemTemplate.loadState.status == RequestState.Failure,
        loadInstitutionItemTemplatesErrorCode: state.institutionItemTemplate.loadState.errorCode,
        savingInstitutionItem: state.institutionItem.saveState && state.institutionItem.saveState.status == RequestState.Pending,
        saveInstitutionItemSuccess: state.institutionItem.saveState && state.institutionItem.saveState.status == RequestState.Success,
        createVersionModalOpen: state.institutionItemsPage.createVersionModalOpen,
        createVersionInstitutionItem: state.institutionItemsPage.institutionItem,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionItemsPageProps> => {
    return {
        loadInstitution: bindActionCreators(InstitutionActions.LoadInstitutionById, dispatch),
        clearInstitution: bindActionCreators(InstitutionActions.Clear, dispatch),
        loadInstitutionItemTemplates: bindActionCreators(InstitutionItemTemplateActions.LoadInstitutionItemTemplate, dispatch),
        clearInstitutionItemTemplates: bindActionCreators(InstitutionItemTemplateActions.Clear, dispatch),
        saveInstitutionItem: bindActionCreators(InstitutionItemActions.SaveInstitutionItem, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        setEditModelViewState: bindActionCreators(InstitutionItemsPageActions.setEditModalViewState, dispatch),
        setCreateVersionModelViewState: bindActionCreators(InstitutionItemsPageActions.setCreateVersionModalViewState, dispatch),
        loadInstitutionItemCategories: bindActionCreators(InstitutionItemCategoryActions.LoadInstitutionItemCategoriesByInstitutionId, dispatch),
        clearInstitutionItemCategories: bindActionCreators(InstitutionItemCategoryActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InstitutionItemsPage);
